import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  Phone,
  Stethoscope,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOCTOR } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "confirmed", "completed", "cancelled"];

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-900 border-amber-300",
  confirmed: "bg-pine-100 text-pine-800 border-pine-300",
  completed: "bg-pine-950 text-bone border-pine-950",
  cancelled: "bg-stone-100 text-stone-500 border-stone-300",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const waLink = (a) =>
  `https://wa.me/918433746205?text=${encodeURIComponent(
    `Appointment Request\nName: ${a.name}\nAge / Sex: ${a.age ?? "-"} / ${a.sex ?? "-"}\nPhone: ${a.phone}\nEmail: ${a.email}\nPreferred: ${a.preferred_date} · ${a.time_slot}\nConcern: ${a.concern}\nMessage: ${a.message || "-"}\nRef: ${a.id.slice(0, 8).toUpperCase()}`
  )}`;

const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setAuth(true);
      } catch {
        setAuth(false);
        navigate("/admin/login", { replace: true });
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/appointments`, { withCredentials: true });
      setAppointments(res.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Could not load appointments.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (auth) fetchAppointments();
  }, [auth, fetchAppointments]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/appointments/${id}`,
        { status },
        { withCredentials: true }
      );
      setAppointments((appts) => appts.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update the status.");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* clear client side regardless */
    }
    navigate("/admin/login", { replace: true });
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="animate-spin text-pine-500" size={32} />
      </div>
    );
  }

  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: appointments.filter((a) => (a.status || "new") === s).length }),
    {}
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-semibold text-pine-500">
              <span className="h-px w-8 bg-pine-500" /> Appointment Requests
            </span>
            <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950">
              Welcome, {user?.name || "Doctor"}
            </h1>
            <p className="mt-2 text-sm text-sage">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pine-900 border border-pine-900/20 rounded-full px-6 py-3 hover:border-pine-500 hover:text-pine-500 transition-colors duration-300 self-start"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {STATUSES.map((s) => (
            <div key={s} className="bg-white border border-mist p-6" data-testid={`stat-${s}`}>
              <div className="font-serif text-4xl text-pine-950">{counts[s]}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-sage capitalize">{s}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="animate-spin text-pine-500" size={28} />
            </div>
          ) : appointments.length === 0 ? (
            <div
              data-testid="appointments-empty"
              className="bg-white border border-mist p-14 text-center text-sage"
            >
              <Stethoscope size={32} className="mx-auto text-pine-300" />
              <p className="mt-4 font-serif text-2xl text-pine-950">No appointment requests yet.</p>
              <p className="mt-2 text-sm">New requests from the website will appear here instantly.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="appointments-list">
              {appointments.map((a, idx) => (
                <article
                  key={a.id}
                  data-testid={`appointment-card-${idx}`}
                  className="bg-white border border-mist p-6 lg:p-8 hover:border-pine-500/40 transition-colors duration-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-serif text-2xl text-pine-950">{a.name}</h2>
                        {(a.age !== undefined || a.sex) && (
                          <span className="text-sm text-sage">
                            {a.age !== undefined && a.age !== "" ? `${a.age} yrs` : ""}
                            {a.age !== undefined && a.age !== "" && a.sex ? " · " : ""}
                            {a.sex || ""}
                          </span>
                        )}
                        <span
                          data-testid={`appointment-status-${idx}`}
                          className={`text-[10px] uppercase tracking-[0.18em] font-semibold border px-3 py-1 rounded-full capitalize ${STATUS_STYLES[a.status || "new"]}`}
                        >
                          {a.status || "new"}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-sage">
                        <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-pine-500 transition-colors">
                          <Phone size={14} /> {a.phone}
                        </a>
                        <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-pine-500 transition-colors">
                          <Mail size={14} /> {a.email}
                        </a>
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays size={14} /> {a.preferred_date} · {a.time_slot}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock size={14} /> Requested {formatDate(a.created_at)}
                        </span>
                      </div>
                      <div className="mt-3 text-sm">
                        <span className="font-semibold text-pine-900">{a.concern}</span>
                        {a.message && <span className="text-sage"> — {a.message}</span>}
                      </div>
                      <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-sage">
                        Ref {a.id.slice(0, 8).toUpperCase()}
                      </div>
                    </div>

                    <div className="flex lg:flex-col gap-3 shrink-0">
                      <Select value={a.status || "new"} onValueChange={(s) => updateStatus(a.id, s)}>
                        <SelectTrigger
                          data-testid={`status-select-${idx}`}
                          aria-label="Update appointment status"
                          className="rounded-full border-mist w-40 h-11 focus:ring-pine-500"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}-${idx}`} className="capitalize">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <a
                        href={waLink(a)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-button-${idx}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#1FA855] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#178a45] transition-colors duration-300"
                      >
                        <MessageCircle size={15} />
                        WhatsApp Summary
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Admin;
